import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserCheck, UserX, Shield, ShieldOff, KeyRound, Ban, PlayCircle, Trash2 } from "lucide-react";
import { API } from "../lib/api";

/**
 * Admin — Users & Coach Approvals + account lifecycle.
 * Endpoints:
 *   GET    /admin/users                       list
 *   PUT    /admin/users/{id}/coach            promote/approve/suspend-coach/demote
 *   PUT    /admin/users/{id}/status           suspend / reactivate account
 *   POST   /admin/users/{id}/send-reset       email a password-reset link
 *   DELETE /admin/users/{id}                  permanent delete + order anonymise
 */
export default function AdminUsers() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null); // user object being deleted
  const [confirmText, setConfirmText] = useState("");

  const authHeader = () => ({
    headers: { Authorization: "Basic " + btoa(`${username}:${password}`) },
  });

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users`, authHeader());
      setUsers(r.data);
      setAuthed(true);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Invalid credentials." : "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/admin/users`, authHeader());
      setUsers(r.data);
    } catch {
      toast.error("Failed to refresh.");
    }
  };

  const withBusy = async (id, fn) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try { await fn(); }
    finally {
      setBusy((b) => { const { [id]: _, ...rest } = b; return rest; });
    }
  };

  const updateCoach = (u, patch, msg) => withBusy(u.id, async () => {
    try {
      await axios.put(`${API}/admin/users/${u.id}/coach`, patch, authHeader());
      toast.success(msg);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    }
  });

  const setStatus = (u, suspended) => withBusy(u.id, async () => {
    try {
      await axios.put(`${API}/admin/users/${u.id}/status`, { suspended }, authHeader());
      toast.success(suspended ? "Account suspended." : "Account reactivated.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    }
  });

  const sendReset = (u) => withBusy(u.id, async () => {
    if (!window.confirm(`Send a password-reset email to ${u.email}?`)) return;
    try {
      const r = await axios.post(`${API}/admin/users/${u.id}/send-reset`, {}, authHeader());
      toast.success(r.data?.email_delivered ? `Reset email sent to ${u.email}.` : `Reset link generated (email delivery unavailable).`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send reset link.");
    }
  });

  const openDelete = (u) => { setDeleteTarget(u); setConfirmText(""); };
  const cancelDelete = () => { setDeleteTarget(null); setConfirmText(""); };
  const confirmDelete = () => withBusy(deleteTarget.id, async () => {
    try {
      await axios.delete(`${API}/admin/users/${deleteTarget.id}`, authHeader());
      toast.success(`${deleteTarget.email} deleted. Their orders were anonymised.`);
      cancelDelete();
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  });

  const statusPill = (u) => {
    if (u.suspended) return <span className="text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-red-500/60 text-red-400">Suspended</span>;
    if (!u.is_coach) return <span className="text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-white/20 text-white/50">Customer</span>;
    if (u.coach_approved) return <span className="text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-[#2A5BFF] text-[#2A5BFF]">Coach · Approved</span>;
    return <span className="text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-yellow-500/60 text-yellow-500">Coach · Pending</span>;
  };

  return (
    <div className="min-h-screen bg-[#050510] text-white" data-testid="admin-users-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <div className="flex items-center gap-6 text-[11px] tracking-[0.3em] uppercase text-white/60">
          <Link to="/" className="hover:text-[#2A5BFF]">← Back</Link>
          <Link to="/admin" className="hover:text-[#2A5BFF]">Waitlist</Link>
          <Link to="/admin/services" className="hover:text-[#2A5BFF]">Services</Link>
          <Link to="/admin/audit-log" className="hover:text-[#2A5BFF]">Audit</Link>
          <Link to="/admin/orders" className="hover:text-[#2A5BFF]" data-testid="admin-users-orders-link">Orders</Link>
        </div>
        <h1 className="font-anton uppercase text-5xl md:text-6xl mt-8">Admin — Users & Coaches</h1>

        {!authed && (
          <form onSubmit={load} className="mt-10 max-w-md border border-white/10 p-6" data-testid="admin-users-login">
            <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Username</label>
            <input className="input-line focus-ring" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" data-testid="admin-users-username" required />
            <label className="mt-6 block text-[10px] tracking-[0.32em] uppercase text-white/50">Password</label>
            <input type="password" className="input-line focus-ring" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="admin-users-password" required />
            <button type="submit" disabled={loading} className="btn-primary mt-8 px-6 py-3 text-[11px] tracking-[0.24em] uppercase disabled:opacity-60" data-testid="admin-users-login-button">
              {loading ? "…" : "Sign In"}
            </button>
          </form>
        )}

        {authed && (
          <div className="mt-10" data-testid="admin-users-content">
            <div className="text-[11px] tracking-[0.28em] uppercase text-white/50 mb-6">
              {users.length} users · {users.filter((u) => u.is_coach && u.coach_approved).length} approved coaches · {users.filter((u) => u.suspended).length} suspended
            </div>

            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] tracking-[0.28em] uppercase text-white/40 border-b border-white/10">
                  <tr>
                    <th className="p-4 text-left">First Name</th>
                    <th className="p-4 text-left">Email</th>
                    <th className="p-4 text-left">Joined</th>
                    <th className="p-4 text-left">Status</th>
                    <th className="p-4 text-right w-[520px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className={`border-b border-white/5 hover:bg-white/[0.02] ${u.suspended ? "opacity-70" : ""}`} data-testid={`admin-user-row-${u.id}`}>
                      <td className="p-4">{u.first_name}</td>
                      <td className="p-4 text-white/70">{u.email}</td>
                      <td className="p-4 text-white/50">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="p-4">{statusPill(u)}</td>
                      <td className="p-4 text-right">
                        <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                          {!u.is_coach && (
                            <button onClick={() => updateCoach(u, { is_coach: true, coach_approved: false }, "Promoted to coach (pending approval).")} disabled={!!busy[u.id]} className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase" data-testid={`promote-${u.id}`}>
                              <Shield size={12} /> Promote
                            </button>
                          )}
                          {u.is_coach && !u.coach_approved && (
                            <button onClick={() => updateCoach(u, { coach_approved: true }, "Coach approved.")} disabled={!!busy[u.id]} className="btn-primary inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase" data-testid={`approve-${u.id}`}>
                              <UserCheck size={12} /> Approve
                            </button>
                          )}
                          {u.is_coach && u.coach_approved && (
                            <button onClick={() => updateCoach(u, { coach_approved: false }, "Coach suspended.")} disabled={!!busy[u.id]} className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase hover:border-yellow-500 hover:text-yellow-500" data-testid={`suspend-coach-${u.id}`}>
                              <UserX size={12} /> Suspend Coach
                            </button>
                          )}
                          {u.is_coach && (
                            <button onClick={() => { if (!window.confirm(`Demote ${u.email} back to a normal customer?`)) return; updateCoach(u, { is_coach: false }, "Demoted to customer."); }} disabled={!!busy[u.id]} className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase hover:border-red-500 hover:text-red-500" data-testid={`demote-${u.id}`}>
                              <ShieldOff size={12} /> Demote
                            </button>
                          )}
                          <button onClick={() => sendReset(u)} disabled={!!busy[u.id]} className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase hover:border-[#2A5BFF] hover:text-[#2A5BFF]" data-testid={`reset-${u.id}`}>
                            <KeyRound size={12} /> Reset PW
                          </button>
                          {u.suspended ? (
                            <button onClick={() => setStatus(u, false)} disabled={!!busy[u.id]} className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase hover:border-green-500 hover:text-green-400" data-testid={`reactivate-${u.id}`}>
                              <PlayCircle size={12} /> Reactivate
                            </button>
                          ) : (
                            <button onClick={() => { if (!window.confirm(`Suspend ${u.email}? They will be signed out immediately and unable to log in.`)) return; setStatus(u, true); }} disabled={!!busy[u.id]} className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase hover:border-red-500 hover:text-red-400" data-testid={`suspend-account-${u.id}`}>
                              <Ban size={12} /> Suspend
                            </button>
                          )}
                          <button onClick={() => openDelete(u)} disabled={!!busy[u.id]} className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase text-red-400 hover:border-red-500 hover:text-red-500" data-testid={`delete-${u.id}`}>
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-white/40 text-sm">No users yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="delete-user-modal">
          <div className="max-w-lg w-full border border-red-500/40 bg-[#0a0a15] p-8">
            <div className="text-[11px] tracking-[0.32em] uppercase text-red-400">Delete member</div>
            <h2 className="font-anton uppercase text-3xl mt-3">Permanently delete this account?</h2>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              <span className="text-white">{deleteTarget.first_name}</span> · <span className="text-white/80">{deleteTarget.email}</span><br/>
              Their orders will remain in the system but with PII stripped (user_id nulled, email hashed for regulatory trace).
              Every active session is invalidated. Uploaded files stay in storage until a scheduled purge (V1.1).
            </p>
            <label className="mt-6 block text-[10px] tracking-[0.32em] uppercase text-white/50">Type <span className="text-red-400">DELETE</span> to confirm</label>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="input-line focus-ring" data-testid="delete-user-confirm-input" autoFocus />
            <div className="mt-6 flex items-center gap-3 justify-end">
              <button type="button" onClick={cancelDelete} className="btn-ghost px-4 py-2 text-[10px] tracking-[0.24em] uppercase" data-testid="delete-user-cancel">Cancel</button>
              <button type="button" onClick={confirmDelete} disabled={confirmText !== "DELETE" || !!busy[deleteTarget.id]} className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.24em] uppercase bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed" data-testid="delete-user-confirm">
                <Trash2 size={12} /> Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
